// Industry intelligence.
//
// Redesigned 2026-09-20 after Mike answered three questions by pop-up:
//
//   "Tell me what to do."   The page's job is to turn the week's trade press into a handful of
//   moves, so the CONSEQUENCE leads every card and the article's own headline becomes its
//   provenance, set below it in muted type. That inversion is the whole design; everything else
//   here follows Tasks and triage exactly.
//
//   "Lead with mine, the rest below."   Overturns the earlier choice that everyone saw the same
//   list in the same order with a small mark on the ones that were theirs. The ranking is still
//   computed once and shared — a reader's own items are lifted out of it, never reordered within
//   it — so two people discussing "the Genius piece" are still discussing the same list.
//
//   "I am not sure it needs to exist because it is counter intuitive to the function of
//   intelligence."   The footprint filter rail is gone. Asking the reader to filter by territory,
//   right, product and counterparty was asking them to do the machine's job. What the rail carried
//   survives on each card as the targets, which say why the item is here and open the record.
//
// An article carries several consequences, not one: on the live store "Genius Sports launches
// prediction markets affiliate" drew five, about Kalshi, Genius, DraftKings, Polymarket and a US
// right we hold. The heaviest leads and the others fold under it. Only a brain's sentence counts;
// a rule's line ("Fan Duel is a prospect with no open deal.") never takes a line on the card, and
// an item that has none of its own drops below the fold and shows its headline instead.
//
// Read: one press takes a card off the reader's page, held eight seconds with an Undo before it is
// sent, exactly as triage holds a Complete, so the undo IS the hold and nothing is written until it
// has passed. What it writes is the reader's own completion of the item. Nobody else's page moves.
import { ActionButton, ActionRow, ActionInput, CardFrame, Chip, EmptyState, SectionHeading } from './parts/index.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { routeId } from './route-id.js';
import { ApiError, Unauthenticated, type Store } from './api.js';
import { EMPTY_FILTERS, industry, whenWords, type ArchiveView, type IndustryItem, type IndustryView } from './industry/api.js';
import { IndustryItemPage } from './IndustryItemPage.js';
import { useLiveRefresh } from './live.js';
import { TargetName, TargetNames } from './company/names.js';
import './industry.css';


/**
 * The band an article belongs to. The page is read downward like a news site: today, yesterday,
 * then the days behind them by name, then older ones by date.
 */
/** The reader's own calendar day for an article, as YYYY-MM-DD, for grouping and sorting. */
export function dayKey(at: string): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return '0000-00-00';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function dayBand(at: string, now: Date = new Date()): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return 'Undated';
  const midnight = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((midnight(now) - midnight(d)) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return d.toLocaleDateString(undefined, { weekday: 'long' });
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'long' });
}

/** How the page names an item: the sentence it leads with, which is what the reader just read. */
const nameOf = (item: IndustryItem): string => item.implications[0]?.text ?? item.title;

type Load = { kind: 'loading' } | { kind: 'ready'; view: IndustryView } | { kind: 'error'; message: string };

/** The undo window on Read, the same eight seconds triage holds a Complete for. */
export const HOLD_MS = 8_000;



// -------------------------------------------------------------------------------------------
// The card
// -------------------------------------------------------------------------------------------

interface CardProps {
  item: IndustryItem;
  store: Store;
  view: IndustryView;
  go: (path: string) => void;
  reload: () => void;
  onRead: (item: IndustryItem) => void;
}

function Card({ item, store, view, go, reload, onRead }: CardProps) {
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [parking, setParking] = useState(false);
  const [alsoOpen, setAlsoOpen] = useState(false);
  const [until, setUntil] = useState('');

  const act = async (body: Parameters<typeof industry.decide>[1]) => {
    setBusy(true); setProblem(null);
    try {
      const r = await industry.decide(item.newsItemId, { store: store.tenantId, ...body });
      if (r.action === 'open' && r.openHref) { go(r.openHref); return; }
      const lines: string[] = [];
      if (r.landedIn) lines.push(`In ${r.landedIn.name === undefined ? 'their' : r.landedIn.name + "'s"} ten at ${r.landedIn.rankNo}.`);
      if (r.landingRefused) lines.push(r.landingRefused);
      if (r.toldOwner) lines.push(`${r.toldOwner.name} was told.`);
      if (r.removal) lines.push(r.removal.scope === 'everyone' ? 'Removed for everyone. You can undo it for the next hour.' : 'Hidden for you alone.');
      if (r.parkId) lines.push('Parked. It comes back on the date you gave.');
      setSaid(lines.join(' ') || 'Recorded.');
      setParking(false);
      if (r.removal || r.parkId) window.setTimeout(reload, 1200);
    } catch (e) {
      if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
      setProblem(e instanceof ApiError || e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  const owner = item.owner;
  const open = () => go(`/intelligence/industry/${item.newsItemId}`);

  // The lead sentence and the ones folded under it. The API orders them heaviest first and keeps
  // `implication` equal to the first, so slicing here can never disagree with what leads.
  const also = item.implications.slice(1);
  // Below the fold there is no brain's sentence to lead with, so the headline leads instead.
  const leads = item.implications.length > 0 ? item.implication : item.title;
  const headlineIsLead = item.implications.length === 0;

  return (
    <CardFrame className={item.regulator ? 'ind-card reg' : 'ind-card'} data-news-item={item.newsItemId}>
      <div className="ind-meta">
        {item.regulator ? <Chip className="ind-chip reg">Regulator</Chip> : null}
        <Chip className="ind-chip">{item.publisher}</Chip>
        {item.forMe && item.forMeBecause ? <Chip className="ind-chip you">{item.forMeBecause}</Chip> : null}
        <span className="ind-when">{whenWords(item.at)}</span>
      </div>

      <h3 className="ind-says">
        <ActionButton reading type="button" onClick={open}>{leads}</ActionButton>
      </h3>

      {headlineIsLead ? null : (
        <p className="ind-src">
          <ActionButton reading type="button" onClick={open}>{item.title}</ActionButton>
        </p>
      )}

      {item.targets.length > 0 ? (
        <div className="ind-touches">
          {item.targets.slice(0, 6).map((t, i) => (
            <span key={`${t.kind}-${t.id ?? t.name}-${i}`} className="ind-tch">
              <b><TargetName t={t} go={go} /></b> <em>{t.note}</em>
            </span>
          ))}
          {item.targets.length > 6 ? <span className="ind-tch more">and {item.targets.length - 6} more</span> : null}
        </div>
      ) : null}

      {also.length > 0 ? (
        <div className={alsoOpen ? 'ind-also open' : 'ind-also'}>
          <ActionButton reading type="button" className="ind-alsoh" aria-expanded={alsoOpen} onClick={() => setAlsoOpen((v) => !v)}>
            <span className="caret" aria-hidden="true" />
            And for us it also means <span className="n">{also.length}</span>
          </ActionButton>
          {alsoOpen ? (
            <ul>
              {also.map((im, i) => <li key={i}><b>{im.targetName}</b> {im.text}</li>)}
            </ul>
          ) : null}
        </div>
      ) : null}

      {item.declined ? (
        <div className="ind-note">
          <b>{item.declined.lifecycle === 'paused' ? 'Paused' : 'Declined'} on {item.declined.at.slice(0, 10)}, in their words:</b>{' '}
          “{item.declined.reason}”{' '}
          <ActionButton reading type="button" className="ind-link" onClick={() => go(`/matters/${item.declined!.matterId}`)}>Correct this</ActionButton>
        </div>
      ) : null}

      {item.removedForEveryone ? (
        <div className="ind-note warn">
          Removed for everyone by {item.removedForEveryone.byName}, undoable until {whenWords(item.removedForEveryone.undoableUntil)}.{' '}
          <ActionButton type="button" className="ind-link" disabled={busy} onClick={async () => {
            setBusy(true);
            try { await industry.undo(item.newsItemId, store.tenantId, item.removedForEveryone!.id); } catch (e) { setProblem(e instanceof Error ? e.message : String(e)); }
            setBusy(false); reload();
          }}>Put it back</ActionButton>
        </div>
      ) : null}

      {item.taken.length > 0 ? (
        <div className="ind-note">
          {item.taken.map((t, i) => (
            <div key={i}>{t.action === 'take' ? `${t.byName} took this` : `${t.byName} raised it with ${t.toName ?? 'the owner'}`}, {whenWords(t.at)}</div>
          ))}
        </div>
      ) : null}

      <div className="ind-opts">
        {owner ? (
          <ActionButton type="button" className="ind-opt lead" disabled={busy} onClick={() => void act({ action: 'raise', ownerPrincipalId: owner.principalId })}>
            Raise it with {owner.name}
          </ActionButton>
        ) : (
          <ActionButton type="button" className="ind-opt lead" disabled={busy} onClick={() => void act({ action: 'take' })}>
            Take it myself
          </ActionButton>
        )}
        {owner ? (
          <ActionButton type="button" className="ind-opt" disabled={busy} onClick={() => void act({ action: 'take' })}>Take it myself</ActionButton>
        ) : null}
        <ActionButton type="button" className="ind-opt" disabled={busy} onClick={() => onRead(item)}>Mark as read</ActionButton>
        <ActionButton reading type="button" className="ind-opt" disabled={busy} onClick={open}>Open the article</ActionButton>
      </div>

      {parking ? (
        <div className="ind-park">
          <label htmlFor={`until-${item.newsItemId}`}>Come back on</label>
          <ActionInput id={`until-${item.newsItemId}`} type="date" value={until} onChange={(e) => setUntil(e.target.value)} />
          <ActionButton type="button" className="ind-opt" disabled={busy || !until} onClick={() => void act({ action: 'park', until: new Date(`${until}T09:00:00`).toISOString() })}>Park it</ActionButton>
          <ActionButton reading type="button" className="ind-link" onClick={() => setParking(false)}>Cancel</ActionButton>
        </div>
      ) : null}

      <ActionRow variant="standing" className="ind-standing" actions={[
        { key: 'park', label: 'Park until…', onClick: () => setParking((p) => !p) },
        { key: 'not-relevant', label: 'Not relevant', disabled: busy, onClick: () => void act({ action: 'not_relevant' }) },
        ...(view.isTrainer ? [{ key: 'not-relevant-anyone', label: 'Not relevant for anyone', disabled: busy,
          onClick: () => void act({ action: 'not_relevant', forEveryone: true }) }] : []),
        { key: 'why', label: 'Why is this here?', opensOnly: true, onClick: open },
      ]} />

      {said ? <div className="ind-said">{said}</div> : null}
      {problem ? <div className="ind-said bad" role="alert">Cobrain could not do that. {problem}</div> : null}
    </CardFrame>
  );
}

// -------------------------------------------------------------------------------------------
// The archive
// -------------------------------------------------------------------------------------------

/**
 * Everything ever read, searched. The page shows the recent days; this reaches the rest.
 *
 * Measured on the live store 2026-09-21, 98 of 216 articles were outside the page's seven-day
 * window and could not be reached from any screen. Nothing ages out now, and a search says how
 * far back what it looked through goes, so a result of none is never mistaken for a gap.
 */
function Search({ store, go }: { store: Store; go: (path: string) => void }) {
  const [text, setText] = useState('');
  const [touches, setTouches] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [found, setFound] = useState<ArchiveView | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const asked = text.trim() !== '' || touches.trim() !== '' || from !== '' || to !== '';

  const run = async () => {
    if (!asked) { setFound(null); return; }
    setBusy(true); setProblem(null);
    try {
      setFound(await industry.archive(store.tenantId, { text, touches, from, to }));
    } catch (e) {
      if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
      setProblem(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  const clear = () => { setText(''); setTouches(''); setFrom(''); setTo(''); setFound(null); setProblem(null); };

  return (
    <div className="ind-search">
      <form className="ind-searchrow" onSubmit={(e) => { e.preventDefault(); void run(); }}>
        <ActionInput reading
          type="search" value={text} placeholder="Search everything we have ever read"
          aria-label="Search everything we have ever read"
          onChange={(e) => setText(e.target.value)}
        />
        <ActionButton reading type="button" className={open ? 'ind-opt on' : 'ind-opt'} aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          Narrow it
        </ActionButton>
        {/* Not the yellow lead: the one filled bar on this page is a card's decision, and a page
            whose loudest element is its search box is telling the reader to search rather than
            to read. This is an ordinary option in ink. */}
        <ActionButton reading type="submit" className="ind-opt strong" disabled={busy}>{busy ? 'Searching' : 'Search'}</ActionButton>
        {found || asked ? <ActionButton reading type="button" className="ind-link" onClick={clear}>Clear</ActionButton> : null}
      </form>

      {open ? (
        <div className="ind-narrow">
          <label>
            <span>Touching</span>
            <ActionInput reading type="text" value={touches} placeholder="An account, a right, a market" onChange={(e) => setTouches(e.target.value)} />
          </label>
          <label><span>From</span><ActionInput reading type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
          <label><span>To</span><ActionInput reading type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
        </div>
      ) : null}

      {problem ? <div className="ind-said bad" role="alert">The archive could not be read. {problem}</div> : null}

      {found ? (
        <div className="ind-results">
          <p className="ind-resulth">
            {found.total === 0
              ? `Nothing answers that. ${found.readTotal} article${found.readTotal === 1 ? '' : 's'} searched${found.oldest ? `, back to ${whenWords(found.oldest)}` : ''}.`
              : `${found.total} of ${found.readTotal} article${found.readTotal === 1 ? '' : 's'}${found.total > found.items.length ? `, showing the newest ${found.items.length}` : ''}.`}
          </p>
          {found.items.map((i) => (
            <p key={i.newsItemId} className="ind-result">
              <ActionButton reading type="button" onClick={() => go(`/intelligence/industry/${i.newsItemId}`)}>
                {i.implications[0]?.text ?? i.title}
              </ActionButton>
              <span>{i.publisher}, {whenWords(i.at)}{i.targets.length > 0 ? <>, touches <TargetNames targets={i.targets.slice(0, 3)} go={go} /></> : ''}</span>
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// -------------------------------------------------------------------------------------------
// The bands
// -------------------------------------------------------------------------------------------

function Band({ title, count, children, folded, onToggle, note }: {
  title: string; count: number; children?: React.ReactNode;
  folded?: boolean; onToggle?: () => void; note?: string;
}) {
  const foldable = onToggle !== undefined;
  return (
    <section className="ind-band">
      {foldable ? (
        <ActionButton reading type="button" className={folded ? 'ind-bandh folded' : 'ind-bandh'} aria-expanded={!folded} onClick={onToggle}>
          <span className="caret" aria-hidden="true" />
          <h2>{title}</h2>
          <span className="n">{count}</span>
          {note ? <span className="ind-bandnote">{note}</span> : null}
        </ActionButton>
      ) : (
        <SectionHeading className="ind-bandh" title={title} note={<><span className="n">{count}</span>{note ? <span className="ind-bandnote">{note}</span> : null}</>} />
      )}
      {folded ? null : children}
    </section>
  );
}

// -------------------------------------------------------------------------------------------
// The page
// -------------------------------------------------------------------------------------------

export function Industry({ store, path, go }: { store: Store; path: string; go: (path: string) => void }) {
  const parts = path.split('/').filter(Boolean);
  const itemId = parts[0] === 'intelligence' && parts[1] === 'industry' && parts[2] ? routeId(parts[2]) : null;
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [tick, setTick] = useState(0);
  const [downOpen, setDownOpen] = useState(false);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  // A news page that is as old as the moment it was opened is the fault it was rebuilt to fix.
  // This re-reads when the page gets attention again rather than on a clock: no beat is passed,
  // because nobody has timed this read. The effect below keeps the list on screen while it
  // re-reads, so a refresh never flashes the page back to a skeleton.
  useLiveRefresh(reload);

  // ---- Read, held for the undo ----
  const [readIds, setReadIds] = useState<ReadonlySet<string>>(() => new Set());
  const [held, setHeld] = useState<IndustryItem | null>(null);
  const [readProblem, setReadProblem] = useState<string | null>(null);
  const heldRef = useRef<{ item: IndustryItem; timer: number } | null>(null);
  const forget = (id: string) => setReadIds((s) => { const n = new Set(s); n.delete(id); return n; });
  const sendRead = (item: IndustryItem) => {
    industry.decide(item.newsItemId, { store: store.tenantId, action: 'read' }).catch((e: unknown) => {
      if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
      forget(item.newsItemId);
      setReadProblem(`“${nameOf(item)}” could not be marked read. ${e instanceof Error ? e.message : String(e)}`);
    });
  };
  const settleRead = () => {
    const h = heldRef.current;
    heldRef.current = null; setHeld(null);
    if (h) { window.clearTimeout(h.timer); sendRead(h.item); }
  };
  const markRead = (item: IndustryItem) => {
    if (heldRef.current) settleRead();
    setReadProblem(null);
    setReadIds((s) => new Set(s).add(item.newsItemId));
    const h = { item, timer: 0 };
    h.timer = window.setTimeout(() => { if (heldRef.current === h) settleRead(); }, HOLD_MS);
    heldRef.current = h; setHeld(item);
  };
  const undoRead = () => {
    const h = heldRef.current;
    if (!h) return;
    window.clearTimeout(h.timer);
    heldRef.current = null; setHeld(null);
    forget(h.item.newsItemId);
  };
  useEffect(() => {
    const leave = () => {
      const h = heldRef.current;
      if (!h) return;
      heldRef.current = null; window.clearTimeout(h.timer);
      industry.decideOnLeave(h.item.newsItemId, { store: store.tenantId, action: 'read' });
    };
    window.addEventListener('pagehide', leave);
    return () => { window.removeEventListener('pagehide', leave); leave(); };
  }, [store.tenantId]);

  useEffect(() => {
    if (itemId) return;
    let cancelled = false;
    setLoad((l) => (l.kind === 'ready' ? l : { kind: 'loading' }));
    industry.list(store.tenantId, EMPTY_FILTERS)
      .then((view) => { if (!cancelled) setLoad({ kind: 'ready', view }); })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
        setLoad({ kind: 'error', message: e instanceof Error ? e.message : String(e) });
      });
    return () => { cancelled = true; };
  }, [store.tenantId, tick, itemId]);

  if (itemId) return <IndustryItemPage key={`${store.tenantId}:${itemId}`} store={store} newsItemId={itemId} go={go} />;

  if (load.kind === 'loading') {
    return (
      <div className="industry">
        <header className="ind-top"><h1>Industry</h1></header>
        <div className="ind-skeleton" aria-hidden="true"><i /><i /><i /></div>
      </div>
    );
  }
  if (load.kind === 'error') {
    return (
      <div className="industry">
        <header className="ind-top"><h1>Industry</h1></header>
        <div className="ind-failed" role="alert">
          <p>Industry could not be read. Cobrain said: {load.message}</p>
          <p>Nothing was changed. <ActionButton reading type="button" className="ind-link" onClick={reload}>Try again</ActionButton></p>
        </div>
      </div>
    );
  }

  const { view } = load;
  const shown = view.items.filter((i) => !readIds.has(i.newsItemId));
  const down = view.digest.filter((i) => !readIds.has(i.newsItemId));

  const card = (item: IndustryItem) => (
    <Card key={item.newsItemId} item={item} store={store} view={view} go={go} reload={reload} onRead={markRead} />
  );

  // The days.
  //
  // The API orders by day, then by whose it is, then by weight, but it groups by the SERVER's
  // day, which is UTC, and the band is named in the READER's day. An article filed at 23:00 UTC
  // is yesterday to the server and today to somebody in Paris, so the two disagree either side of
  // midnight and the page drew "Thursday" twice with a Wednesday between them. The reader's day
  // has to win, so the grouping is redone here, stably: the API's order inside a day survives and
  // only the boundaries move.
  const byDay = [...shown].sort((a, b) => dayKey(b.at).localeCompare(dayKey(a.at)));
  const days: Array<{ label: string; items: IndustryItem[] }> = [];
  for (const item of byDay) {
    const label = dayBand(item.at);
    const last = days[days.length - 1];
    if (last && last.label === label) last.items.push(item);
    else days.push({ label, items: [item] });
  }

  const readLine = view.articlesRead === 0
    ? 'No article has come in yet from any feed.'
    : `${view.articlesRead} article${view.articlesRead === 1 ? '' : 's'} from ${view.publishersRead} publisher${view.publishersRead === 1 ? '' : 's'} in the last ${view.windowDays} days.`;

  return (
    <div className="industry">
      <header className="ind-top">
        <h1>Industry</h1>
        <p className="ind-read">{readLine}</p>
      </header>

      <Search store={store} go={go} />

      {shown.length === 0 && down.length === 0 ? (
        <EmptyState className="ind-empty" title="Nothing in the trade press touches anything of ours."
          note={<>{readLine} Cobrain read every one of them against the accounts, deals, rights and markets on record and drew no consequence from any.</>} />
      ) : (
        <>
          {days.map((d) => (
            <Band key={d.label} title={d.label} count={d.items.length}>
              {d.items.map(card)}
            </Band>
          ))}

          {down.length > 0 ? (
            <Band
              title="Touched something, nothing worth a move"
              count={down.length}
              folded={!downOpen}
              onToggle={() => setDownOpen((v) => !v)}
              note="Nothing here changes anything for us."
            >
              <div className="ind-down">
                {down.map((d) => (
                  <p key={d.newsItemId}>
                    <ActionButton reading type="button" onClick={() => go(`/intelligence/industry/${d.newsItemId}`)}>{d.title}</ActionButton>
                    <span>{d.publisher}, touches {d.targets.length > 0 ? <TargetNames targets={d.targets} go={go} /> : 'nothing of ours'}</span>
                  </p>
                ))}
              </div>
            </Band>
          ) : null}
        </>
      )}

      {readProblem ? <div className="ind-said bad" role="alert">Cobrain could not do that. {readProblem}</div> : null}

      <div className={held ? 'ind-hold on' : 'ind-hold'} role="status" aria-live="polite">
        {held ? <><span>Read, off your page: <b>{nameOf(held)}</b></span><ActionButton type="button" onClick={undoRead}>Undo</ActionButton></> : null}
      </div>
    </div>
  );
}
